import React from 'react';

import { distanceGreaterThan } from '@paragon/geometry-library';
import {
  calculateBoundedPlane,
  calculateBoundedPlaneInProgress,
  calculateOverhangsAndSubfasciaeForBoundedPlane,
  getRoofPlaneOverhang3DTranslation,
  getTotalPlaneElevation,
  isRoofPlane,
} from '@paragon/layout';

import { useAppSelector } from '../../../../components/hooks';
import { LayoutCoordinateVisuals } from '../../../../library/layout-geometry/LayoutCoordinateVisuals';
import { getActiveCeilingMode, getActiveRoofMode } from '../../../../store/selectors/layoutVisualOptionsSelectors';
import {
  getCalculatedRoofAndCeilingPlanes,
  getCurrentlyPickablePlaneGuids,
  getPickingPlaneInfo,
  getPlanesToDisplay,
} from '../../../../store/selectors/roofAndCeilingPlaneSelectors';
import { useAutoInvalidateOnChange } from '../../../hooks/invalidation-hooks';
import { layoutColors } from '../../layoutColors';
import { getRoofFaceColor, usePlaneStyleCalculator } from '../../polygon-rendering/common/style';
import { LayoutPolygonsEdgesCollection } from '../../polygon-rendering/edges/LayoutPolygonsEdgesCollection';
import { LayoutPolygonsEdgesRenderer } from '../../polygon-rendering/edges/LayoutPolygonsEdgesRenderer';
import { PlaneVisual } from './PlaneVisual';

// FIXME: implement 2D version of roof plane visuals so we can use z-index approach for better 2D "render order"

export function PlanesController() {
  const roofMode = useAppSelector(getActiveRoofMode);
  const ceilingMode = useAppSelector(getActiveCeilingMode);

  const planesToDisplay = useAppSelector(getPlanesToDisplay);
  const planesDictionary = useAppSelector(getCalculatedRoofAndCeilingPlanes);

  const pickablePlaneGuids = useAppSelector(getCurrentlyPickablePlaneGuids);
  const pickingRealPlane = useAppSelector((state) => getPickingPlaneInfo(state)?.activeMode === 'real');

  const planeGuidBeingCut = useAppSelector((state) => {
    const pickingPlaneInfo = getPickingPlaneInfo(state);
    if (!pickingPlaneInfo) return null;
    switch (pickingPlaneInfo.targetInfo.type) {
      case 'ceiling-cut':
      case 'roof-cut':
        return pickingPlaneInfo.targetInfo.planeGuidToCut;
      default:
        return null;
    }
  });

  useAutoInvalidateOnChange(roofMode, ceilingMode, planesToDisplay);

  const getStyle = usePlaneStyleCalculator();

  const planeVisuals = [];

  const roofSegments = new LayoutPolygonsEdgesCollection();
  const ceilingSegments = new LayoutPolygonsEdgesCollection();

  let keyFallbackIndex = 0;

  for (let i = 0; i < planesToDisplay.length; i++) {
    const plane = planesToDisplay[i];

    const applicableMode = isRoofPlane(plane) ? roofMode : ceilingMode;
    const applicableSegments = isRoofPlane(plane) ? roofSegments : ceilingSegments;

    if (applicableMode === 'none') continue;

    const style = getStyle(plane);

    const bounds =
      'guid' in plane && plane.guid === planeGuidBeingCut
        ? calculateBoundedPlaneInProgress(plane, planesDictionary)
        : calculateBoundedPlane(plane, planesDictionary);

    const colorFromStyle = getRoofFaceColor(style);
    const colorForPlaneEdges = applicableMode === 'solid' ? layoutColors.solidMeshEdges : colorFromStyle;

    applicableSegments.addFromBounds(bounds, colorForPlaneEdges);

    let overhangRegions;
    let subfasciaRegions;

    if (isRoofPlane(plane)) {
      const { overhangs: overhangRegions, subfasciae: subfasciaRegions } =
        calculateOverhangsAndSubfasciaeForBoundedPlane(plane, bounds);

      if (overhangRegions) {
        for (const polygon of overhangRegions) applicableSegments.addPolygonEdges(polygon, colorFromStyle);
      } else if (distanceGreaterThan(plane.overhang, 0)) {
        const baseLineElevation = getTotalPlaneElevation(plane);
        const overhangTranslation = getRoofPlaneOverhang3DTranslation(plane, { subtractSubfascia: false });

        const definingGeometry = plane.calculatedGeometry.segment ?? plane.calculatedGeometry.line;
        applicableSegments.add(definingGeometry.to3D(baseLineElevation).translate(overhangTranslation), colorFromStyle);
      }

      if (subfasciaRegions) {
        for (const polygon of subfasciaRegions) applicableSegments.addPolygonEdges(polygon, colorFromStyle);
      }
    }

    planeVisuals.push(
      <PlaneVisual
        key={'guid' in plane ? plane.guid : keyFallbackIndex++}
        style={style}
        mode={applicableMode}
        plane={plane}
        calculatedBounds={bounds.shape}
        overhangRegions={overhangRegions}
        subfasciaRegions={subfasciaRegions}
        pickable={pickingRealPlane && 'guid' in plane && pickablePlaneGuids.has(plane.guid)}
      />,
    );
  }

  return (
    <LayoutCoordinateVisuals>
      <group position-z={1 / 64}>
        {planeVisuals}

        <LayoutPolygonsEdgesRenderer segments={roofSegments} />
        <LayoutPolygonsEdgesRenderer segments={ceilingSegments} />
      </group>
    </LayoutCoordinateVisuals>
  );
}
